import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map from '@components/atoms/Map/Map';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Typography, TypographyAppearanceVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Container } from '@components/atoms/Container';
import { Space } from '@components/atoms/Space';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useThemes } from '@hooks/useThemes';
import styles from './ServiceProfile.module.css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { useData } from '@hooks/useData';

export interface ServiceProfileProps {
    category?: {
        uuid?: string;
        name?: string;
    };
    name: string;
    merchantName: string;
    logo?: {
        src?: string;
        type?: string;
        height?: number;
        width?: number;
        isDefaultLogo?: boolean;
    };
    email: string;
    phoneNumber: string;
    address: string[];
    locations?: {
        uuid: string;
        latitude: number;
        longitude: number;
    }[];
    schedule: { header?: string; text: string }[];
    bannerImage?: {
        src?: string;
        type?: string;
        height?: number;
        width?: number;
    };
    link?: {
        label: string;
        url: string;
    } | null;
    socialLinks: SocialMediaProps['socialLinks'];
    onEditClickBanner?: () => void;
    onEditClickLogo?: () => void;
    onEditClickNameCaesUrlAndSocials?: () => void;
    onEditClickAddressAndContacts?: () => void;
}

export const ServiceProfile: FC<ServiceProfileProps> = ({
    category,
    merchantName,
    name,
    logo,
    email,
    phoneNumber,
    address,
    locations,
    schedule,
    bannerImage,
    link,
    socialLinks,
    onEditClickBanner,
    onEditClickLogo,
    onEditClickNameCaesUrlAndSocials,
    onEditClickAddressAndContacts,
}) => {
    const pageSize = useMediaQuery();
    const { useGrayscaleTheme } = useThemes();
    const { allSettings } = useData();
    const { t } = useTranslation();

    const [openBannerLightbox, setOpenBannerLightbox] = useState(false);
    const bannerSlides = bannerImage?.src ? [{ src: bannerImage.src }] : [];

    const [openLogoLightbox, setOpenLogoLightbox] = useState(false);
    const logoSlides = logo?.src ? [{ src: logo.src }] : [];

    const translations = {
        addressLabel: t('service-profile-component.address.label'),
        contactsLabel: t('service-profile-component.contacts.label'),
        scheduleLabel: t('service-profile-component.schedule.label'),
        phoneDescription: t('service-profile-component.phone-description'),
        mapAltText: t('service-profile-component.map.alt-text'),
        bannerAlt: t('service-profile-component.banner.alt'),
        logoAlt: t('service-profile-component.logo.alt'),
    };

    return (
        <Container
            classes={`px-0 service-profile ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`}
            customSize="custom-large">
            <>
                <div className="position-relative" data-testid="service-profile">
                    {/* for edition */}
                    {onEditClickBanner ? (
                        <>
                            {bannerImage?.src ? (
                                <img
                                    src={bannerImage?.src}
                                    className={`w-100 ${styles.banner}`}
                                    alt={translations.bannerAlt}
                                    onClick={() => setOpenBannerLightbox(true)}
                                    style={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <div className={styles['placeholder-banner']}></div>
                            )}
                            <button className="m-4 position-absolute end-0 top-0 text-decoration-none">
                                <RoundIcon
                                    icon="fa-light fa-pen-to-square"
                                    variant={RoundIconVariants.variant1}
                                    onClick={onEditClickBanner}
                                />
                            </button>
                        </>
                    ) : (
                        // only display banner if it exists
                        <>
                            {bannerImage?.src && (
                                <img
                                    src={bannerImage?.src}
                                    className={`w-100 ${styles.banner}`}
                                    alt={translations.bannerAlt}
                                    onClick={() => setOpenBannerLightbox(true)}
                                />
                            )}
                        </>
                    )}
                </div>
                <div className="bg-secondary position-relative">
                    {/* company logo */}
                    <div className="position-absolute text-center top-0 start-50 translate-middle">
                        {logo?.src && (
                            <img
                                src={logo?.src}
                                className={`rounded-circle ${styles.logo}`}
                                alt={translations.logoAlt}
                                onClick={() => setOpenLogoLightbox(true)}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: logo?.isDefaultLogo
                                        ? allSettings?.data?.layouts?.find((item) => item.name === 'primary color')?.value
                                        : 'white',
                                    ...(logo?.isDefaultLogo ? { padding: '12px' } : {}),
                                }}
                            />
                        )}

                        {/* for edition */}
                        {onEditClickLogo && (
                            <button className="position-absolute start-50 translate-middle bottom-0 text-decoration-none">
                                <RoundIcon
                                    icon="fa-light fa-pen-to-square"
                                    variant={RoundIconVariants.variant1}
                                    onClick={onEditClickLogo}
                                />
                            </button>
                        )}
                    </div>

                    <Space spaceBreakpoints={{ xs: { space: 50 } }} />

                    <div className="row py-3 mx-0 align-items-center">
                        <div className="col-12 col-md-4 py-3 ps-4 pe-3 text-center text-md-start">
                            <Typography classes="mt-4 mb-2" appearanceLevel={TypographyAppearanceVariants.level5}>
                                {name}
                            </Typography>
                            {category && category.name && (
                                <Typography classes="text-truncate text-uppercase mb-0" color="muted" fontSize="12">
                                    {merchantName}
                                </Typography>
                            )}
                        </div>

                        <SocialMedia socialLinks={socialLinks} />

                        {link?.label && link?.url && (
                            <div
                                className={`col-12 col-md-${onEditClickNameCaesUrlAndSocials ? '3' : '4'} py-3 px-3 text-center text-md-end`}>
                                <Anchor href={link.url} mode={AnchorMode.secondary} target="_blank">
                                    {link.label}
                                </Anchor>
                            </div>
                        )}

                        {onEditClickNameCaesUrlAndSocials && (
                            <div className="position-relative col-12 col-md-1 py-3 px-3">
                                {/* for editing */}
                                <div
                                    className={
                                        'text-decoration-none p-0 mx-md-4 position-absolute' +
                                        ` ${['xs', 'sm'].includes(pageSize) ? 'pt-3 top-50 start-50 translate-middle' : 'top-0 end-0'}`
                                    }>
                                    <RoundIcon
                                        icon="fa-light fa-pen-to-square"
                                        variant={RoundIconVariants.variant1}
                                        onClick={onEditClickNameCaesUrlAndSocials}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <Map
                    locations={
                        locations?.map((location) => ({
                            uuid: location.uuid,
                            name: name ?? '',
                            latitude: location.latitude,
                            longitude: location.longitude,
                        })) ?? []
                    }
                />
                <div className={`${styles['information-in-columns']} px-2 px-md-5`}>
                    {/* for edition */}
                    {onEditClickAddressAndContacts && (
                        <button className="p-0 m-4 position-absolute end-0 top-0 text-decoration-none">
                            <RoundIcon
                                icon="fa-light fa-pen-to-square"
                                variant={RoundIconVariants.variant1}
                                onClick={onEditClickAddressAndContacts}
                            />
                        </button>
                    )}
                    {(address.length > 0 || phoneNumber || email || schedule.length > 0) && (
                        <div className={`${styles['contacts-info']} row text-center text-light py-3 pb-md-4`}>
                            {address.every((line) => line) ? (
                                <div className="col-12 col-md-4 py-3 px-3">
                                    <Typography classes="mb-3" fontWeight="800" fontSize="16">
                                        {translations.addressLabel}
                                    </Typography>
                                    {address.map((addressLine, index) => (
                                        <Typography key={`address-line-${index}`} fontSize="16">
                                            {addressLine}
                                        </Typography>
                                    ))}
                                </div>
                            ) : (
                                <div className="col-12 col-md-4 py-3 px-3"></div>
                            )}

                            {phoneNumber || email ? (
                                <div className="col-12 col-md-4 py-3 px-3">
                                    <Typography classes="mb-3" fontWeight="800" fontSize="16">
                                        {translations.contactsLabel}
                                    </Typography>
                                    {phoneNumber && <Typography fontSize="16">{`${phoneNumber}*`}</Typography>}
                                    {email && (
                                        <Typography fontSize="16">
                                            <Anchor mailTo={email} classes="text-light" mode={AnchorMode.none}>
                                                {email}
                                            </Anchor>
                                        </Typography>
                                    )}
                                    {phoneNumber && (
                                        <Typography classes="mt-3" fontSize="12">
                                            {`*${translations.phoneDescription}`}
                                        </Typography>
                                    )}
                                </div>
                            ) : (
                                <div className="col-12 col-md-4 py-3 px-3"></div>
                            )}

                            {schedule.some((item) => item.header || item.text) ? (
                                <div className="col-12 col-md-4 py-3 px-3">
                                    <Typography classes="mb-3" fontWeight="800" fontSize="16">
                                        {translations.scheduleLabel}
                                    </Typography>
                                    {schedule.map((item, index) => (
                                        <Fragment key={`schedule-item-${index}`}>
                                            {index !== 0 && <Space spaceBreakpoints={{ xs: { space: 15 } }} />}
                                            {item.header && (
                                                <Typography fontWeight="800" fontSize="16">
                                                    {item.header}
                                                </Typography>
                                            )}
                                            <Typography fontSize="16">
                                                {item.text &&
                                                    item.text.split(/\r?\n/).map((line, index) => (
                                                        <Fragment key={index}>
                                                            {line}
                                                            <br />
                                                        </Fragment>
                                                    ))}
                                            </Typography>
                                        </Fragment>
                                    ))}
                                </div>
                            ) : (
                                <div className="col-12 col-md-4 py-3 px-3"></div>
                            )}
                        </div>
                    )}
                </div>
            </>
            <Lightbox
                open={openBannerLightbox}
                close={() => setOpenBannerLightbox(false)}
                slides={bannerSlides}
                index={0}
                plugins={[]}
                toolbar={{
                    buttons: ['close'],
                }}
                controller={{
                    closeOnBackdropClick: true,
                }}
            />
            <Lightbox
                open={openLogoLightbox}
                close={() => setOpenLogoLightbox(false)}
                slides={logoSlides}
                index={0}
                plugins={[]}
                toolbar={{
                    buttons: ['close'],
                }}
                controller={{
                    closeOnBackdropClick: true,
                }}
            />
            {/* cspell:disable */}
            {openBannerLightbox || openLogoLightbox ? (
                <style>{`
                    .yarl__navigation_prev,
                    .yarl__navigation_next {
                        display: none !important;
                        visibility: hidden !important;
                    }
                `}</style>
            ) : (
                <></>
            )}
            {/* cspell:enable */}
        </Container>
    );
};

export interface SocialMediaProps {
    socialLinks: {
        name?: string;
        icon?: string;
        url?: string;
    }[];
}

export const SocialMedia: FC<SocialMediaProps> = ({ socialLinks }) => {
    return (
        <div className="col-12 col-md-4 py-3 px-3 px-sm-0 text-center">
            <ul className="nav d-inline-flex justify-content-center">
                {socialLinks
                    .filter((item) => item.url)
                    .map((item, index) => (
                        <li key={`social-link-${index}`} className="nav-item px-2" title={item.name}>
                            <Anchor
                                href={item.url}
                                mode={AnchorMode.none}
                                classes="nav-link p-0 text-body-secondary text-decoration-none"
                                target="_blank"
                                rel="noopener noreferrer">
                                <RoundIcon icon={item.icon} variant={RoundIconVariants.variant1} />
                            </Anchor>
                        </li>
                    ))}
            </ul>
        </div>
    );
};
